import React from 'react';

import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useSelector } from '@lumapps/redux/react';
import { useRouter } from '@lumapps/router';
import { useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';

import { LEARNING_TYPES_TO_RESOURCE_PREVIEW_TYPES, LINK_PREVIEW_RESOURCE_TYPES } from '../../../constants';
import { WREX_LINK_PREVIEW } from '../../../keys';
import type { LinkPreviewAttributes } from '../../../types';
import { mapPickerResourceToPreview, type PickerResource } from '../../../utils/mapPickerResourceToPreview';

/**
 * Hook that creates an onConfirm callback for resource pickers
 * @param onChange - Callback to confirm when a valid resource is selected
 * @param onClose - Callback to close the dialog
 */
export const useOnConfirmResourceReplacement = (
    onChange: (preview: LinkPreviewAttributes) => void,
    onClose: () => void,
) => {
    const translate = useTranslate();
    const router = useRouter();
    const { error } = useNotification();
    const currentLanguage = useSelector(currentLanguageSelector);

    return React.useCallback(
        (typeOfResource: LINK_PREVIEW_RESOURCE_TYPES) => {
            const mapper = mapPickerResourceToPreview(typeOfResource);

            return (items: Array<PickerResource[typeof typeOfResource]>) => {
                if (items?.length) {
                    const item = items[0];

                    const learningResourceTypes = [
                        LINK_PREVIEW_RESOURCE_TYPES.LEARNING_PATH,
                        LINK_PREVIEW_RESOURCE_TYPES.RECOMMENDATION_QUIZ,
                        LINK_PREVIEW_RESOURCE_TYPES.SCORM_TRAINING,
                        LINK_PREVIEW_RESOURCE_TYPES.TRAINING_COURSE,
                    ];

                    const isLearningResourceType = learningResourceTypes.includes(typeOfResource);

                    // we check that the current item is a learning resource, has a type and that type is not in LEARNING_TYPES_TO_RESOURCE_PREVIEW_TYPES
                    // then we trigger an error to notify the user that the current learning type doesn't exist
                    if (
                        isLearningResourceType &&
                        'type' in item &&
                        !(item.type in LEARNING_TYPES_TO_RESOURCE_PREVIEW_TYPES)
                    ) {
                        error({ translate: WREX_LINK_PREVIEW.RESOURCE_NOT_HANDLED });
                        return;
                    }

                    onChange(mapper(items[0], translate, currentLanguage, router));
                }
                onClose();
            };
        },
        [onClose, onChange, translate, currentLanguage, router, error],
    );
};
